import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import AppButton from "../button/AppButton";
import AppSelectInput from "../inputs/AppSelectInput";
import AppTextInput from "../inputs/AppTextInput";
import { CreateAdminAccountRequest } from "../../models/RequestModels/CreateAdminAccountRequest";
import { AdminSubscriptionResponse } from "../../models/ResponseModels/AdminSubscriptionResponse";
import { useAppSelector } from "../../store/hooks";

interface CreateAccountModalProps {
  open: boolean;
  subscriptions: AdminSubscriptionResponse[];
  onClose: () => void;
  onSubmit: (body: CreateAdminAccountRequest) => void;
}

const createInitialState = (subscriptionId = 0): CreateAdminAccountRequest => ({
  account_name: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  password: "",
  subscription_id: subscriptionId,
  subscription_status: "trial",
  duration_type: "days",
  duration: 14,
  custom_unit_limit: 21,
  custom_unit_price_amount: 100,
  custom_unit_price_currency: "EUR",
  notes: "",
});

const getSubscriptionLabel = (subscription: AdminSubscriptionResponse) => {
  const limit =
    subscription.property_limit === null
      ? "Unlimited properties"
      : `${subscription.property_limit} properties`;

  const visibility = subscription.is_public ? "public" : "admin only";

  return `${subscription.name} - ${limit} - ${visibility}`;
};

const CreateAccountModal = ({
  open,
  subscriptions,
  onClose,
  onSubmit,
}: CreateAccountModalProps) => {
  const { saving, fieldErrors } = useAppSelector(
    (state) => state.adminAccounts,
  );

  const defaultSubscriptionId = useMemo(() => {
    const freeSubscription = subscriptions.find(
      (subscription) => subscription.code === "free",
    );

    return freeSubscription?.id || subscriptions[0]?.id || 0;
  }, [subscriptions]);

  const [form, setForm] = useState<CreateAdminAccountRequest>(
    createInitialState(defaultSubscriptionId),
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      subscription_id:
        previous.subscription_id ||
        defaultSubscriptionId ||
        subscriptions[0]?.id ||
        0,
    }));
  }, [defaultSubscriptionId, open, subscriptions]);

  const subscriptionOptions = useMemo(
    () =>
      subscriptions.map((subscription) => ({
        label: getSubscriptionLabel(subscription),
        value: String(subscription.id),
      })),
    [subscriptions],
  );

  const selectedSubscription = subscriptions.find(
    (subscription) => subscription.id === Number(form.subscription_id),
  );

  const isCustomSubscription = selectedSubscription?.code === "custom";

  const updateField = (
    key: keyof CreateAdminAccountRequest,
    value: string | number,
  ) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    const body: CreateAdminAccountRequest = {
      ...form,
      subscription_id: Number(form.subscription_id),
      duration: Number(form.duration),
    };

    if (!isCustomSubscription) {
      delete body.custom_unit_limit;
      delete body.custom_unit_price_amount;
      delete body.custom_unit_price_currency;
    }

    onSubmit(body);
  };

  const handleClose = () => {
    setForm(createInitialState(defaultSubscriptionId));
    onClose();
  };

  const getError = (key: string) => fieldErrors[key]?.[0];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Create owner account</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
              },
              gap: 2,
            }}
          >
            <Box sx={{ gridColumn: "1 / -1" }}>
              <AppTextInput
                label="Account name"
                value={form.account_name}
                onChange={(value) => updateField("account_name", value)}
                error={getError("account_name")}
                required
              />
            </Box>

            <AppTextInput
              label="Owner first name"
              value={form.first_name}
              onChange={(value) => updateField("first_name", value)}
              error={getError("first_name")}
              required
            />

            <AppTextInput
              label="Owner last name"
              value={form.last_name || ""}
              onChange={(value) => updateField("last_name", value)}
              error={getError("last_name")}
            />

            <AppTextInput
              label="Owner email"
              type="email"
              value={form.email}
              onChange={(value) => updateField("email", value)}
              error={getError("email")}
              required
            />

            <AppTextInput
              label="Phone"
              value={form.phone || ""}
              onChange={(value) => updateField("phone", value)}
              error={getError("phone")}
            />

            <AppTextInput
              label="Temporary password"
              type="password"
              value={form.password}
              onChange={(value) => updateField("password", value)}
              error={getError("password")}
              required
            />

            <AppSelectInput
              label="Subscription"
              value={String(form.subscription_id || "")}
              onChange={(value) =>
                updateField("subscription_id", Number(value))
              }
              error={getError("subscription_id")}
              disabled={subscriptions.length === 0}
              options={subscriptionOptions}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, 1fr)",
              },
              gap: 2,
            }}
          >
            <AppSelectInput
              label="Status"
              value={form.subscription_status}
              onChange={(value) =>
                updateField(
                  "subscription_status",
                  value as CreateAdminAccountRequest["subscription_status"],
                )
              }
              error={getError("subscription_status")}
              options={[
                { label: "Trial", value: "trial" },
                { label: "Paid", value: "active" },
              ]}
            />

            <AppSelectInput
              label="Duration type"
              value={form.duration_type}
              onChange={(value) =>
                updateField(
                  "duration_type",
                  value as CreateAdminAccountRequest["duration_type"],
                )
              }
              error={getError("duration_type")}
              options={[
                { label: "Days", value: "days" },
                { label: "Months", value: "months" },
                { label: "Years", value: "years" },
              ]}
            />

            <AppTextInput
              label="Duration"
              type="number"
              value={form.duration}
              onChange={(value) => updateField("duration", Number(value))}
              error={getError("duration")}
              required
            />
          </Box>

          {isCustomSubscription ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <AppTextInput
                label="Contracted units"
                type="number"
                value={form.custom_unit_limit || 21}
                onChange={(value) =>
                  updateField("custom_unit_limit", Number(value))
                }
                error={getError("custom_unit_limit")}
                required
              />

              <AppTextInput
                label="Monthly price per unit (€)"
                type="number"
                value={(form.custom_unit_price_amount || 100) / 100}
                onChange={(value) =>
                  updateField(
                    "custom_unit_price_amount",
                    Math.round(Number(value) * 100),
                  )
                }
                error={getError("custom_unit_price_amount")}
                required
              />
            </Box>
          ) : null}

          <AppTextInput
            label="Notes"
            value={form.notes || ""}
            onChange={(value) => updateField("notes", value)}
            error={getError("notes")}
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton variant="outlined" onClick={handleClose} disabled={saving}>
          Cancel
        </AppButton>
        <AppButton
          onClick={handleSubmit}
          loading={saving}
          disabled={subscriptions.length === 0}
        >
          Create account
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAccountModal;
